<script
  setup
  lang="ts"
  generic="
    T extends import('@/composables/useWorkspaceOrdering').WorkspaceOrderItem
  "
>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import type {
  WorkspaceMove,
  WorkspaceOrderItem,
} from "@/composables/useWorkspaceOrdering";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    items: readonly T[];
    editing: boolean;
    disabled?: boolean;
    layout?: "list" | "grid";
    handleLabel: string;
    instructions: string;
  }>(),
  {
    disabled: false,
    layout: "list",
  },
);

const emit = defineEmits<{
  move: [move: WorkspaceMove];
  gesture: [active: boolean];
}>();

defineSlots<{
  item(props: {
    item: T;
    index: number;
    canDrag: boolean;
    handleProps: Record<string, unknown>;
  }): unknown;
}>();

interface PointerGesture {
  pointerId: number;
  handle: HTMLElement;
  sourceId: string;
  targetId: string | null;
  startX: number;
  startY: number;
  active: boolean;
}

const gesture = ref<PointerGesture | null>(null);
const listEl = ref<HTMLElement | null>(null);
const announcement = ref("");
const instructionsId = `workspace-order-instructions-${getCurrentInstance()?.uid ?? "list"}`;
let keyboardFocusId: string | null = null;
let suppressClick = false;
let suppressClickTimer: ReturnType<typeof setTimeout> | null = null;
let edgeScrollFrame: number | null = null;
let edgeScrollPoint: { x: number; y: number } | null = null;
const draggingId = computed(() => gesture.value?.sourceId ?? null);
const dropTargetId = computed(() =>
  gesture.value?.active ? gesture.value.targetId : null,
);

function canDrag(item: WorkspaceOrderItem) {
  return props.editing && item.myPrivileges?.mayAdministrate === true;
}

function mayDrag(item: WorkspaceOrderItem) {
  return canDrag(item) && !props.disabled;
}

function placementFor(sourceId: string, targetId: string) {
  const sourceIndex = props.items.findIndex((item) => item.id === sourceId);
  const targetIndex = props.items.findIndex((item) => item.id === targetId);
  return targetIndex < sourceIndex ? "BEFORE" : "AFTER";
}

function beginPointer(event: PointerEvent, item: WorkspaceOrderItem) {
  if (
    gesture.value ||
    !mayDrag(item) ||
    (event.pointerType === "mouse" && event.button !== 0)
  ) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  const handle = event.currentTarget as HTMLElement;
  handle.setPointerCapture(event.pointerId);
  gesture.value = {
    pointerId: event.pointerId,
    handle,
    sourceId: item.id,
    targetId: null,
    startX: event.clientX,
    startY: event.clientY,
    active: false,
  };
}

function movePointer(event: PointerEvent) {
  const current = gesture.value;
  if (!current || current.pointerId !== event.pointerId) return;

  const distance = Math.hypot(
    event.clientX - current.startX,
    event.clientY - current.startY,
  );
  if (!current.active && distance < 5) return;

  event.preventDefault();
  if (!current.active) {
    current.active = true;
    emit("gesture", true);
  }

  current.targetId = pointerTargetAt(event.clientX, event.clientY);
  edgeScrollPoint = { x: event.clientX, y: event.clientY };
  if (scrollAtEdge(event.clientY)) scheduleEdgeScroll();
}

function edgeScrollDelta(position: number, start: number, end: number) {
  const threshold = 48;
  const maximum = 18;
  if (position < start + threshold) {
    const intensity =
      (threshold - Math.max(0, position - start)) / threshold;
    return -Math.max(1, Math.round(maximum * intensity));
  }
  if (position > end - threshold) {
    const intensity =
      (threshold - Math.max(0, end - position)) / threshold;
    return Math.max(1, Math.round(maximum * intensity));
  }
  return 0;
}

function verticalScroller() {
  let candidate = listEl.value?.parentElement ?? null;
  while (candidate && candidate !== document.body) {
    const overflow = globalThis.getComputedStyle(candidate).overflowY;
    if (
      (overflow === "auto" || overflow === "scroll") &&
      candidate.scrollHeight > candidate.clientHeight
    ) {
      return candidate;
    }
    candidate = candidate.parentElement;
  }
  return null;
}

function scrollAtEdge(pointerY: number) {
  const scroller = verticalScroller();
  if (scroller) {
    const bounds = scroller.getBoundingClientRect();
    const delta = edgeScrollDelta(pointerY, bounds.top, bounds.bottom);
    const canScroll =
      (delta < 0 && scroller.scrollTop > 0) ||
      (delta > 0 &&
        scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight);
    if (!delta || !canScroll) return false;
    scroller.scrollBy({ top: delta, behavior: "auto" });
    return true;
  }

  const scrollingElement = document.scrollingElement;
  if (!scrollingElement) return false;
  const delta = edgeScrollDelta(pointerY, 0, window.innerHeight);
  const canScroll =
    (delta < 0 && window.scrollY > 0) ||
    (delta > 0 &&
      window.scrollY + window.innerHeight < scrollingElement.scrollHeight);
  if (!delta || !canScroll) return false;
  window.scrollBy({ top: delta, behavior: "auto" });
  return true;
}

function stopEdgeScroll() {
  if (edgeScrollFrame != null) cancelAnimationFrame(edgeScrollFrame);
  edgeScrollFrame = null;
  edgeScrollPoint = null;
}

function scheduleEdgeScroll() {
  if (edgeScrollFrame != null) return;
  edgeScrollFrame = requestAnimationFrame(() => {
    edgeScrollFrame = null;
    const current = gesture.value;
    const point = edgeScrollPoint;
    if (!current?.active || !point) return;
    const scrolled = scrollAtEdge(point.y);
    current.targetId = pointerTargetAt(point.x, point.y);
    if (scrolled) scheduleEdgeScroll();
  });
}

function pointerTargetAt(x: number, y: number) {
  const target = document
    .elementFromPoint(x, y)
    ?.closest<HTMLElement>("[data-workspace-order-id]");
  const targetId = target?.dataset.workspaceOrderId;
  if (
    targetId &&
    target &&
    listEl.value?.contains(target) &&
    props.items.some((item) => item.id === targetId)
  ) {
    return targetId;
  }
  return null;
}

function finishPointer(event: PointerEvent, cancelled = false) {
  const current = gesture.value;
  if (!current || current.pointerId !== event.pointerId) return;

  if (!cancelled && current.active) {
    current.targetId = pointerTargetAt(event.clientX, event.clientY);
  }

  const move: WorkspaceMove | null =
    !cancelled &&
    current.active &&
    current.targetId &&
    current.targetId !== current.sourceId
      ? {
          targetId: current.sourceId,
          anchorId: current.targetId,
          placement: placementFor(current.sourceId, current.targetId),
        }
      : null;
  const handle = current.handle;
  stopEdgeScroll();
  gesture.value = null;
  if (handle.hasPointerCapture(event.pointerId)) {
    handle.releasePointerCapture(event.pointerId);
  }

  if (current.active) {
    suppressClick = true;
    if (suppressClickTimer) globalThis.clearTimeout(suppressClickTimer);
    suppressClickTimer = globalThis.setTimeout(() => {
      suppressClick = false;
      suppressClickTimer = null;
    }, 250);
  }
  if (current.active) emit("gesture", false);
  if (move) emit("move", move);
}

function blockNavigationClick(event: MouseEvent) {
  if (!suppressClick && !gesture.value?.active) return;
  event.preventDefault();
  event.stopPropagation();
}

function moveWithKeyboard(event: KeyboardEvent, item: WorkspaceOrderItem) {
  const previous = event.key === "ArrowUp" || event.key === "ArrowLeft";
  const next = event.key === "ArrowDown" || event.key === "ArrowRight";
  if (!previous && !next) return;
  if (!canDrag(item)) return;

  event.preventDefault();
  event.stopPropagation();
  if (!mayDrag(item)) return;
  const index = props.items.findIndex((candidate) => candidate.id === item.id);
  const anchor = props.items[index + (previous ? -1 : 1)];
  if (!anchor) return;

  keyboardFocusId = item.id;
  announcement.value = `${item.name}: ${index + (previous ? 0 : 2)}/${props.items.length}`;
  emit("move", {
    targetId: item.id,
    anchorId: anchor.id,
    placement: previous ? "BEFORE" : "AFTER",
  });
}

watch(
  () => props.disabled,
  async (disabled, wasDisabled) => {
    if (disabled || !wasDisabled || !keyboardFocusId) return;
    const id = keyboardFocusId;
    keyboardFocusId = null;
    await nextTick();
    const item = Array.from(
      listEl.value?.querySelectorAll<HTMLElement>(
        "[data-workspace-order-id]",
      ) ?? [],
    ).find((candidate) => candidate.dataset.workspaceOrderId === id);
    item?.querySelector<HTMLElement>("[data-workspace-order-handle]")?.focus();
  },
);

watch(
  () => props.editing,
  (editing) => {
    const current = gesture.value;
    if (editing || !current) return;
    stopEdgeScroll();
    if (current.handle.hasPointerCapture(current.pointerId)) {
      current.handle.releasePointerCapture(current.pointerId);
    }
    gesture.value = null;
    if (current.active) emit("gesture", false);
  },
);

function handleProps(item: WorkspaceOrderItem, index: number) {
  const enabled = canDrag(item);
  const blocked = !mayDrag(item);
  return {
    "aria-label": `${props.handleLabel}: ${item.name}. ${index + 1}/${props.items.length}`,
    "aria-describedby": instructionsId,
    "aria-disabled": String(blocked),
    "aria-grabbed": String(draggingId.value === item.id),
    "data-workspace-order-handle": "",
    disabled: false,
    tabindex: enabled ? 0 : -1,
    title: `${props.handleLabel}: ${item.name}`,
    onClick: (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    },
    onKeydown: (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      moveWithKeyboard(event, item);
    },
    onPointerdown: (event: PointerEvent) => beginPointer(event, item),
    onPointermove: movePointer,
    onPointerup: (event: PointerEvent) => finishPointer(event),
    onPointercancel: (event: PointerEvent) => finishPointer(event, true),
    onLostpointercapture: (event: PointerEvent) => finishPointer(event, true),
  };
}

onBeforeUnmount(() => {
  stopEdgeScroll();
  if (gesture.value?.active) emit("gesture", false);
  gesture.value = null;
  if (suppressClickTimer) globalThis.clearTimeout(suppressClickTimer);
});
</script>

<template>
  <div class="workspace_order_container">
    <p v-if="editing" :id="instructionsId" class="visually_hidden">
      {{ instructions }}
    </p>
    <p class="visually_hidden" role="status" aria-live="polite">
      {{ announcement }}
    </p>
    <div
      v-bind="$attrs"
      ref="listEl"
      class="workspace_order_list"
      :class="[`workspace_order_list--${layout}`, { is_editing: editing }]"
      :role="editing ? 'list' : undefined"
      @click.capture="blockNavigationClick"
    >
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="workspace_order_item"
        :class="{
          is_dragging: draggingId === item.id,
          drop_before:
            dropTargetId === item.id &&
            draggingId !== item.id &&
            placementFor(draggingId!, item.id) === 'BEFORE',
          drop_after:
            dropTargetId === item.id &&
            draggingId !== item.id &&
            placementFor(draggingId!, item.id) === 'AFTER',
        }"
        :data-workspace-order-id="item.id"
        :data-test="`workspace-order-item-${item.id}`"
        :role="editing ? 'listitem' : undefined"
        :aria-posinset="editing ? index + 1 : undefined"
        :aria-setsize="editing ? items.length : undefined"
      >
        <slot
          name="item"
          :item="item"
          :index="index"
          :can-drag="canDrag(item)"
          :handle-props="handleProps(item, index)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.workspace_order_container {
  display: contents;
}

.visually_hidden {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.workspace_order_list--grid {
  display: grid;
}

.workspace_order_item {
  min-width: 0;
  position: relative;
  transition:
    opacity 140ms ease,
    transform 140ms ease,
    box-shadow 140ms ease;
}

.workspace_order_item.is_dragging {
  opacity: 0.58;
  transform: scale(0.985);
}

.workspace_order_list--list .workspace_order_item.drop_before::before,
.workspace_order_list--list .workspace_order_item.drop_after::after {
  background: var(--pv-workspace-order-accent, #34b5ba);
  border-radius: 999px;
  content: "";
  height: 2px;
  left: 0.35rem;
  pointer-events: none;
  position: absolute;
  right: 0.35rem;
  z-index: 4;
}

.workspace_order_list--list .workspace_order_item.drop_before::before {
  top: -1px;
}

.workspace_order_list--list .workspace_order_item.drop_after::after {
  bottom: -1px;
}

.workspace_order_list--grid .workspace_order_item.drop_before,
.workspace_order_list--grid .workspace_order_item.drop_after {
  box-shadow: 0 0 0 2px var(--pv-workspace-order-accent, #34b5ba);
  border-radius: 0.6rem;
}

:deep([data-workspace-order-handle]) {
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

:deep([data-workspace-order-handle]:focus-visible) {
  outline: 2px solid var(--pv-workspace-order-accent, #34b5ba);
  outline-offset: 2px;
}
</style>
